// WaivedFindingsDisclosure — Phase 4 G3.7, Tier 1 (2026-05-30).
//
// Shared collapsed section the three findings panels render below
// their open findings. Waived findings are filtered out of the
// active groups (so they don't inflate the "N findings" counts), and
// surfaced here instead — muted, with their justification — so the
// architect can see what's been accepted and why without the noise.

import { useState } from 'react'
import { CheckCircle2, ChevronDown, ChevronRight } from 'lucide-react'

export interface WaivedFindingItem {
  id: string
  label: string
  justification: string | null
}

export function WaivedFindingsDisclosure({
  items,
}: {
  items: WaivedFindingItem[]
}) {
  const [open, setOpen] = useState(false)
  if (items.length === 0) return null
  return (
    <div className="rounded-md border border-emerald-900/30 bg-emerald-950/10 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-emerald-950/20"
        aria-expanded={open}
      >
        {open ? (
          <ChevronDown className="h-4 w-4 text-emerald-400" />
        ) : (
          <ChevronRight className="h-4 w-4 text-emerald-400" />
        )}
        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
        <span className="text-xs font-medium text-emerald-200">
          {items.length} waived{' '}
          {items.length === 1 ? 'finding' : 'findings'}
        </span>
        <span className="text-[10.5px] text-emerald-300/60">
          accepted — won't block export
        </span>
      </button>
      {open && (
        <ul className="flex flex-col gap-2 px-3 pb-3 pt-1">
          {items.map((it) => (
            <li
              key={it.id}
              className="rounded border border-emerald-900/20 bg-emerald-950/15 p-2 text-xs"
            >
              <div className="text-emerald-100/80 line-through decoration-emerald-700/50">
                {it.label}
              </div>
              {it.justification && (
                <div className="mt-1 text-emerald-200/70">
                  <span className="text-emerald-300/80">waiver: </span>
                  {it.justification}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
